import { readMetaCreateParts } from './metaCreate';

/*
 * Creación a medias (`META_CREATE_PARTIAL`, F2b API §5) guardada por registro para poder
 * Reintentar (mismo cuerpo + `expected` + key) o Eliminar lo creado aunque se cierre el modal.
 * Hoy vive en sessionStorage (esta pestaña, 24 h). Cuando la API mande `pending_creations`, se
 * cambia solo el «store» de abajo (`metaCreatePendingStore`): el modal usa `readMetaCreatePending` /
 * `saveMetaCreatePending` / `clearMetaCreatePending` y la forma de la entrada (`normalizePending`).
 */

const PREFIX = 'terrio.metaCreatePending';
const VERSION = 1;
export const META_CREATE_PENDING_TTL_MS = 24 * 60 * 60 * 1000;

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);

/** Clave por registro: `terrio.metaCreatePending:user_lot:<id>`. */
export function metaCreatePendingKey({ itemKind, id } = {}) {
  const kind = str(itemKind);
  const rid = str(id == null ? '' : String(id));
  return kind && rid ? `${PREFIX}:${kind}:${rid.toLowerCase()}` : null;
}

/** sessionStorage, o nada (modo privado / sin `window`): el pendiente simplemente no se guarda. */
const sessionStore = {
  get(key) {
    try { return window.sessionStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch { /* sin storage: no se guarda */ }
  },
  remove(key) {
    try { window.sessionStorage.removeItem(key); } catch { /* nada */ }
  },
};

export const metaCreatePendingStore = sessionStore;

/**
 * Entrada válida o `null`. Sin `create_id`, cuerpo, `expected` o key no hay nada que retomar.
 * `retryable: false` = el API ya no deja retomar (key rechazada / reusada / sin confirmar): solo Eliminar.
 */
export function normalizePending(raw) {
  const p = obj(raw);
  if (!p) return null;
  const body = obj(p.body);
  const entry = {
    createId: str(p.createId),
    body,
    expected: str(p.expected),
    idempotencyKey: str(p.idempotencyKey),
    campaignId: str(p.campaignId == null ? '' : String(p.campaignId)),
    parts: Array.isArray(p.parts) ? p.parts.filter((x) => obj(x) && str(x.key)) : [],
    failedStep: str(p.failedStep),
    causeCode: str(p.causeCode),
    metaUrl: str(p.metaUrl),
    retryable: p.retryable !== false,
    savedAt: Number.isFinite(Number(p.savedAt)) ? Number(p.savedAt) : null,
  };
  if (!entry.createId || !entry.body || !entry.expected || !entry.idempotencyKey) return null;
  return entry;
}

/**
 * Arma la entrada desde el error del parcial y lo que se mandó al confirmar.
 * @param {object} err `readMetaCreateError` con kind `partial`
 * @param {{ body: object, expected: string, idempotencyKey: string }} sent
 */
export function pendingFromPartial(err, { body, expected, idempotencyKey }, now = Date.now()) {
  return normalizePending({
    createId: err?.createId,
    body,
    expected,
    idempotencyKey,
    campaignId: err?.campaignId,
    parts: Array.isArray(err?.parts) ? err.parts : readMetaCreateParts(err?.parts),
    failedStep: err?.failedStep,
    causeCode: err?.causeCode,
    metaUrl: err?.metaUrl,
    retryable: true,
    savedAt: now,
  });
}

/** El pendiente de ese registro, o `null` (si venció o está roto, se borra). */
export function readMetaCreatePending(record, now = Date.now(), store = metaCreatePendingStore) {
  const key = metaCreatePendingKey(record);
  if (!key) return null;
  const raw = store.get(key);
  if (!raw) return null;
  let parsed = null;
  try { parsed = JSON.parse(raw); } catch { parsed = null; }
  const entry = parsed?.v === VERSION ? normalizePending(parsed.entry) : null;
  if (!entry || entry.savedAt == null || now - entry.savedAt > META_CREATE_PENDING_TTL_MS) {
    store.remove(key);
    return null;
  }
  return entry;
}

/** Guarda (o reemplaza) el pendiente del registro. Una entrada inválida no se guarda. */
export function saveMetaCreatePending(record, entry, store = metaCreatePendingStore) {
  const key = metaCreatePendingKey(record);
  const clean = normalizePending(entry);
  if (!key || !clean) return null;
  store.set(key, JSON.stringify({ v: VERSION, entry: clean }));
  return clean;
}

/** Terminó bien, se eliminó lo creado o se descartó. */
export function clearMetaCreatePending(record, store = metaCreatePendingStore) {
  const key = metaCreatePendingKey(record);
  if (key) store.remove(key);
}
